import { useState, useEffect } from 'react';
import { MapPin, Clock, Users, Star, Calendar, Shield, Phone, MessageCircle, Heart, Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatPrice, categoryColors, cn } from '../../lib/utils';

export function PackageInfo({ tour, onEnquireClick }) {
  const [isSticky, setIsSticky] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const discountPercent = tour.discounted_price
    ? Math.round(((tour.price - tour.discounted_price) / tour.price) * 100)
    : 0;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: tour.name,
          text: `Check out this tour: ${tour.name}`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <>
      {/* Sticky Mobile Bottom Bar */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 lg:hidden',
          'transform transition-transform duration-300',
          isSticky ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-[#004E64]">
                {formatPrice(tour.discounted_price || tour.price)}
              </span>
              {tour.discounted_price && (
                <span className="text-sm text-[#4A4A4A] line-through">
                  {formatPrice(tour.price)}
                </span>
              )}
            </div>
            <span className="text-xs text-[#4A4A4A]">per person</span>
          </div>
          <Button
            onClick={onEnquireClick}
            className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white px-6 py-3 rounded-full"
          >
            Enquire Now
          </Button>
        </div>
      </div>

      {/* Desktop Sidebar Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden sticky top-24">
        {/* Price Header */}
        <div className="bg-gradient-to-r from-[#004E64] to-[#006B87] p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/80 text-sm">Starting from</span>
            {discountPercent > 0 && (
              <Badge className="bg-[#2A9D8F] text-white">
                {discountPercent}% OFF
              </Badge>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold">
              {formatPrice(tour.discounted_price || tour.price)}
            </span>
            {tour.discounted_price && (
              <span className="text-lg text-white/60 line-through">
                {formatPrice(tour.price)}
              </span>
            )}
          </div>
          <span className="text-white/80 text-sm">per person</span>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Quick Info */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-[#4A4A4A]">
              <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div>
                <span className="text-sm text-[#4A4A4A]">Duration</span>
                <p className="font-semibold text-[#1A1A1A]">
                  {tour.duration_days} Days / {tour.duration_nights} Nights
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[#4A4A4A]">
              <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div>
                <span className="text-sm text-[#4A4A4A]">Destination</span>
                <p className="font-semibold text-[#1A1A1A]">{tour.destination}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[#4A4A4A]">
              <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div>
                <span className="text-sm text-[#4A4A4A]">Group Size</span>
                <p className="font-semibold text-[#1A1A1A]">Max {tour.max_group_size || 20} People</p>
              </div>
            </div>
          </div>

          {/* Savings Banner */}
          {tour.discounted_price && (
            <div className="bg-[#2A9D8F]/10 text-[#2A9D8F] px-4 py-3 rounded-xl text-center">
              <span className="font-semibold">🎉 You Save {formatPrice(tour.price - tour.discounted_price)}!</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={onEnquireClick}
              className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-xl py-6 text-lg font-semibold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Enquire Now
            </Button>
            <a 
              href={`https://wa.me/919181268900?text=Hi, I'm interested in ${encodeURIComponent(tour.name)}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Button
                variant="outline"
                className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-xl py-6"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat on WhatsApp
              </Button>
            </a>
          </div>

          {/* Like & Share */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsLiked(!isLiked)}
              className={cn(
                'flex-1 rounded-xl',
                isLiked && 'bg-red-50 border-red-200 text-red-500'
              )}
            >
              <Heart className={cn('w-5 h-5 mr-2', isLiked && 'fill-red-500')} />
              {isLiked ? 'Saved' : 'Save'}
            </Button>
            <Button
              variant="outline"
              onClick={handleShare}
              className="flex-1 rounded-xl"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm text-[#4A4A4A] mb-3">
              <Shield className="w-4 h-4 text-[#2A9D8F]" />
              <span>Safe & Secure Booking</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
              <Phone className="w-4 h-4 text-[#FF6B35]" />
              <span>Need help? <a href="tel:+919181268900" className="text-[#FF6B35] font-medium">+91 9181268900</a></span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
